/**
 * useCampaignStaticTag — read + set the one Static Tag assigned to a campaign.
 * Setting invalidates both this query and the Scheduler list so the chip
 * stays in sync wherever it is shown.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getCampaignTag,
  setCampaignTag,
  type CampaignStaticTag,
} from '@/features/flow-optimizer/services/campaign-tags-api';
import type { CampaignTagProvider } from '@/features/campaigns/tracker/campaign-tracker-access';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';

export const campaignTagKeys = {
  tag: (provider: CampaignTagProvider, campaignId: string) =>
    ['campaign-tag', provider, campaignId] as const,
};

export function useCampaignStaticTag(
  provider: CampaignTagProvider,
  campaignId: string,
  enabled = true,
) {
  return useQuery<CampaignStaticTag | null>({
    queryKey: campaignTagKeys.tag(provider, campaignId),
    queryFn: () => getCampaignTag(provider, campaignId),
    enabled: enabled && Boolean(campaignId),
  });
}

export function useSetCampaignTag(
  provider: CampaignTagProvider,
  campaignId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staticTagId: string | null) =>
      setCampaignTag(provider, campaignId, staticTagId),
    onSuccess: (tag) => {
      queryClient.setQueryData(campaignTagKeys.tag(provider, campaignId), tag);
      void queryClient.invalidateQueries({ queryKey: optimizerCenterKeys.all });
    },
  });
}
